/**
 * Wishlist System - LocalStorage based
 * Manages wishlist functionality across all pages
 */

class Wishlist {
  constructor() {
    this.customerId = document.documentElement.dataset.customerId || '';
    this.storageKey = this.customerId ? 'arcela_wishlist_' + this.customerId : 'arcela_wishlist_guest';
    this.migrateWishlist();
    this.wishlist = this.loadWishlist();
    this.init();
  }

  migrateWishlist() {
    const current = localStorage.getItem(this.storageKey);
    const legacy = localStorage.getItem('pado_wishlist');
    const guest = localStorage.getItem('arcela_wishlist_guest');

    if (!current && !this.customerId && legacy) {
      localStorage.setItem(this.storageKey, legacy);
    }

    if (this.customerId) {
      const merged = this.mergeLists(current, guest || (!current ? legacy : null));
      if (merged) localStorage.setItem(this.storageKey, JSON.stringify(merged));
      if (guest) localStorage.removeItem('arcela_wishlist_guest');
    }
  }

  mergeLists(currentRaw, extraRaw) {
    let current = [];
    let extra = [];
    try { current = currentRaw ? JSON.parse(currentRaw) : []; } catch (error) { current = []; }
    try { extra = extraRaw ? JSON.parse(extraRaw) : []; } catch (error) { extra = []; }
    if (!extra.length) return currentRaw ? null : current;
    extra.forEach(function(item) {
      if (!current.some(function(existing) { return String(existing.id) === String(item.id); })) {
        current.push(item);
      }
    });
    return current;
  }

  init() {
    // Initialize wishlist buttons
    this.updateAllWishlistButtons();
    this.updateWishlistCount();
    
    // Listen for wishlist toggle events
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-wishlist-toggle]')) {
        const button = e.target.closest('[data-wishlist-toggle]');
        const productId = button.dataset.productId;
        const productHandle = button.dataset.productHandle;
        
        if (productId && productHandle) {
          e.preventDefault();
          e.stopPropagation();
          this.toggle(productId, productHandle);
        }
      }
    });

    // Listen for cart updates to sync wishlist UI
    document.addEventListener('cart:updated', () => {
      this.updateAllWishlistButtons();
    });
  }

  loadWishlist() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
      this.updateAllWishlistButtons();
      this.updateWishlistCount();
      
      // Dispatch event
      const event = new CustomEvent('wishlist:updated', { detail: { wishlist: this.wishlist } });
      document.dispatchEvent(event);
    } catch (e) {
      console.error('Error saving wishlist:', e);
    }
  }

  isInWishlist(productId) {
    // Normalize to string for consistent comparison
    const normalizedId = String(productId);
    return this.wishlist.some(item => String(item.id) === normalizedId);
  }

  add(productId, productHandle) {
    // Normalize to string for consistent comparison
    const normalizedId = String(productId);
    if (!this.isInWishlist(normalizedId)) {
      this.wishlist.push({
        id: normalizedId,
        handle: productHandle,
        addedAt: new Date().toISOString()
      });
      this.saveWishlist();
      return true;
    }
    return false;
  }

  remove(productIdOrHandle) {
    // Normalize to string for consistent comparison
    const normalized = String(productIdOrHandle);
    // Try to find by id first, then by handle
    const index = this.wishlist.findIndex(item => 
      String(item.id) === normalized || String(item.handle) === normalized
    );
    if (index > -1) {
      this.wishlist.splice(index, 1);
      this.saveWishlist();
      return true;
    }
    return false;
  }

  toggle(productId, productHandle) {
    // Normalize to string for consistent comparison
    const normalizedId = String(productId);
    if (this.isInWishlist(normalizedId)) {
      this.remove(normalizedId);
      return false;
    } else {
      this.add(normalizedId, productHandle);
      return true;
    }
  }

  getAll() {
    return [...this.wishlist];
  }

  getCount() {
    return this.wishlist.length;
  }

  updateWishlistButton(button) {
    if (!button) return;
    
    const productId = button.dataset.productId;
    if (!productId) return;

    // Normalize productId for comparison
    const normalizedId = String(productId);
    const isActive = this.isInWishlist(normalizedId);

    if (isActive) {
      button.classList.add('product-card__wishlist--active', 'product-stacked-gallery__wishlist--active', 'quick-view-product__wishlist--active');
      button.setAttribute('aria-label', (window.themeStrings && window.themeStrings.removeWishlist) || '');
    } else {
      button.classList.remove('product-card__wishlist--active', 'product-stacked-gallery__wishlist--active', 'quick-view-product__wishlist--active');
      button.setAttribute('aria-label', (window.themeStrings && window.themeStrings.addWishlist) || '');
    }
    
    // Update icon fill for product-card wishlist icon
    const productCardIcon = button.querySelector('.product-card__wishlist-icon');
    if (productCardIcon) {
      const path = productCardIcon.querySelector('path');
      if (path) {
        if (isActive) {
          path.setAttribute('fill', 'currentColor');
        } else {
          path.setAttribute('fill', 'none');
        }
      }
    }
    
    // Update icon fill for stacked gallery wishlist icon
    const stackedGalleryIcon = button.querySelector('.product-stacked-gallery__wishlist-icon');
    if (stackedGalleryIcon) {
      const path = stackedGalleryIcon.querySelector('path');
      if (path) {
        if (isActive) {
          path.setAttribute('fill', 'currentColor');
        } else {
          path.setAttribute('fill', 'none');
        }
      }
    }
    
    // Update icon fill for quick-view wishlist icon
    const quickViewIcon = button.querySelector('.quick-view-product__wishlist-icon');
    if (quickViewIcon) {
      const path = quickViewIcon.querySelector('path');
      if (path) {
        if (isActive) {
          path.setAttribute('fill', 'currentColor');
        } else {
          path.setAttribute('fill', 'none');
        }
      }
    }
  }

  updateAllWishlistButtons() {
    const buttons = document.querySelectorAll('[data-wishlist-toggle]');
    buttons.forEach(button => {
      this.updateWishlistButton(button);
    });
  }

  updateWishlistCount() {
    const countElements = document.querySelectorAll('[data-wishlist-count]');
    const count = this.getCount();
    countElements.forEach(el => {
      el.textContent = count > 0 ? count : '';
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }
}

// Initialize wishlist when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.wishlist = new Wishlist();
  });
} else {
  window.wishlist = new Wishlist();
}

